import React, { useEffect, useState, useCallback, useRef } from "react";
import Button from "../Button/Button";
import Webcam from "react-webcam";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../../css/UploadImage.scss";
import useWindowDimensions from "../../utils/WIndowDimensions";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEraser, faRotateLeft } from "@fortawesome/free-solid-svg-icons";
import Modal from "./Modal";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css"; // make sure you have this
import { getCroppedImg } from "../../utils/crop";
import { getDimensions, getSize, getb64 } from "../../utils/imageProperties";
import { resizeImage } from "../../utils/resizeImage";

/**
 * A hook that returns the bounding rect of the webcam component on resize
 * @param ref A reference to the webcam
 */
const useBoundingRect = (ref: any) => {
  const [boundingRect, setBoundingRect] = useState<DOMRect>({} as DOMRect);

  const handleResize = () => {
    if (ref.current) {
      setBoundingRect(ref.current.video.getBoundingClientRect());
    }
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [ref]);

  return boundingRect;
};

/**
 * Convert base64 data URL to a File object.
 */

/**
 * Resize an image file to 1080p max.
 */
async function resizeImageFile(
  file: File,
  maxWidth = 1080,
  maxHeight = 1080
): Promise<File> {
  return new Promise<File>((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      let { width, height } = img;
      if (width > maxWidth || height > maxHeight) {
        const ratio = width / height;
        if (ratio > 1) {
          width = maxWidth;
          height = maxWidth / ratio;
        } else {
          height = maxHeight;
          width = maxHeight * ratio;
        }
      }

      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject("Cannot get canvas context.");
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob((blob) => {
        if (!blob) {
          reject("Canvas is empty or cannot be converted to blob.");
          return;
        }
        const resizedFile = new File([blob], file.name, {
          type: file.type,
          lastModified: Date.now(),
        });
        resolve(resizedFile);
      }, file.type);
    };

    img.onerror = (err) => {
      reject(err);
    };

    img.src = url;
  });
}

/**
 * If the server fails, use the original File -> base64 fallback
 * so we can still submit via the "Done" button.
 */

/**
 * Interface for the PhotoBoothModal component
 */
interface inputs {
  open?: boolean;
  didClose: () => void;
  didSetImage: (image: string) => void;
  patronID?: string;
}

const PhotoBoothModal = ({
  open = false,
  didClose,
  didSetImage,
  patronID,
}: inputs) => {
  const webcamRef = useRef<Webcam>(null);
  const hiddenFileInputRef = useRef<HTMLInputElement>(null);

  const windowDim = useWindowDimensions();
  const resizedBoundingBox: DOMRect = useBoundingRect(webcamRef);

  /** States */
  const [srcImage, setSrcImage] = useState({ selected: "", temp: "" });
  const [originalImage, setOriginalImage] = useState<string>("");
  const [canLoadCamOverlays, setCanLoadCamOverlays] = useState(false);
  const [webcamBoundingBox, setWebcamBoundingBox] = useState<DOMRect>(
    {} as DOMRect
  );
  const [isBackgroundRemoved, setIsBackgroundRemoved] = useState(false);
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  /** Crop Tool States */
  const [showCropTool, setShowCropTool] = useState(false);
  const [dimensions, setDimensions] = useState("");
  const [size, setSize] = useState(0);
  const [crop, setCrop] = useState({
    unit: "%" as "px" | "%",
    width: 30,
    height: 30,
    x: 0,
    y: 0,
  });

  let imageRef = useRef<HTMLImageElement>(null);
  let fileUrl: any;

  // Change this for your server path:
  let baseURL = "/background-removal-server";

  const [webcamStyles, setWebcamStyles] = useState({
    width: "90%",
  });

  /** Notifications */
  useEffect(() => {
    if (open) {
      toast.info(
        (): JSX.Element => (
          <div
            className="bg-blue-100 border-t border-b border-blue-500 text-blue-700 px-4 py-3"
            role="alert"
          >
            <p className="font-bold">
              All new profile images are required to follow these guidelines:
            </p>
            <ul className="text-sm list-disc ml-2">
              <li>
                Current <b>color photo</b> of yourself.
              </li>
              <li>
                White or blue smooth background (
                <b>no other people or objects in photo</b>).
              </li>
              <li>
                <b>Sufficient lighting</b> with no shadows.
              </li>
              <li>Front view (full-face filling at least 80% of the frame).</li>
              <li>
                Cropped from just above the top of head to the collarbone.
              </li>
              <li>Eyes open and visible.</li>
              <li>No hats or glasses.</li>
            </ul>
            <section className="flex flex-col justify-center items-center align-center">
              <h4 className="font-bold pb-4">
                Example of a Good Profile Photo
              </h4>
              <img
                src="http://catcard.ucmerced.edu/sites/catcard.ucmerced.edu/files/page/images/cat_card_freshman_photo.jpg"
                width="100"
                height="100"
                alt="example good id profile"
              />
            </section>
            <footer className="flex justify-center">Tap to close.</footer>
          </div>
        ),
        {
          position: "top-right",
          autoClose: 1000000,
          hideProgressBar: true,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
          toastId: "custom-toast-id",
          onClose: () => {
            // if (windowDim.width < 768) {
            //   tipNotif(1000);
            // }
          },
        }
      );

      // if (windowDim.width > 768) {
      //   tipNotif(2000);
      // }
    }
  }, [open]);

  // After bounding box is set, let overlays load
  useEffect(() => {
    if (resizedBoundingBox.height !== 0 && resizedBoundingBox.width !== 0) {
      setWebcamBoundingBox(resizedBoundingBox);
      setCanLoadCamOverlays(true);
    }
  }, [resizedBoundingBox]);

  // On bigger screens, narrower camera
  useEffect(() => {
    if (windowDim.width > 768) {
      setWebcamStyles({
        width: "50%",
      });
    }
  }, [windowDim]);

  /** Helper to show the smaller tip pop-up */
  // const tipNotif = (delayMillisec: number) => {
  //   toast(
  //     (): JSX.Element => (
  //       <div className="flex flex-col justify-center items-center align-center text-white">
  //         <p>Please center your face in the circle</p>
  //         <p>No hats or glasses</p>
  //       </div>
  //     ),
  //     {
  //       position: "top-center",
  //       autoClose: false,
  //       hideProgressBar: false,
  //       closeOnClick: true,
  //       pauseOnHover: true,
  //       toastId: "custom-toast-id-2",
  //       delay: delayMillisec,
  //       style: {
  //         background: "rgba(0,40,86,.8)",
  //       },
  //     }
  //   );
  // };

  const reset = () => {
    setSrcImage({ selected: "", temp: "" });
    setOriginalImage("");
    setIsBackgroundRemoved(false);
    setCanLoadCamOverlays(false);
    toast.dismiss();
  };

  const handleUploadButtonClick = () => {
    if (hiddenFileInputRef.current) {
      hiddenFileInputRef.current.click();
    }
  };

  // 1) A tiny helper to convert a File or Blob to base64
  /**
   * Converts a base64 data URL into a File object.
   */
  function dataURLToFile(dataURL: string, fileName: string): File {
    const arr = dataURL.split(",");
    const mimeMatch = arr[0].match(/:(.*?);/);
    if (!mimeMatch) {
      toast.error("Cannot parse MIME type from Data URL.");
      throw new Error("Cannot parse MIME type from Data URL.");
    }
    const mime = mimeMatch[1];
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    return new File([u8arr], fileName, { type: mime });
  }

  async function getImageSize(url: string): Promise<number> {
    const response = await fetch(url);
    const blob = await response.blob();
    return blob.size; // Returns the size in bytes
  }

  async function dataURLToBlob(dataURL: string): Promise<Blob> {
    const response = await fetch(dataURL);
    return await response.blob();
  }

  /**
   * File input change:
   *  1) preview the selected file
   *  2) resize it to max 1080p
   *  3) send to server
   *  4) server returns a cropped image blob or base64
   *  5) on error, fallback to local data URL
   */
  const fileInputChangeHandler = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files && event.target.files[0];
    if (!file) return;

    // Workaround: Block Files app uploads on mobile
    if (isMobile) {
      const fileSource = file.name.toLowerCase();

      // If the file is from iCloud, Downloads, or another storage service, block it
      const isFromFilesApp =
        fileSource.includes("icloud") ||
        fileSource.includes("downloads") ||
        fileSource.includes("pdf");

      if (isFromFilesApp) {
        toast.error(
          "Please select an image from your Photo Library, not Files."
        );
        reset();
        return;
      }
    }

    // 1) Keep the original file in state
    // 2) Show a local preview
    console.log(`Original file size: ${(file.size / 1024).toFixed(2)} KB`);
    let imageURL: string = await resizeImage(file);
    const resizedImageSize = await getImageSize(imageURL);
    console.log(
      `Resized image size: ${(resizedImageSize / 1024).toFixed(2)} KB`
    );
    setSrcImage({ selected: imageURL, temp: "" });
    setOriginalImage(imageURL);

    // Attempt server calls
    try {
      // Convert the resized image URL to a Blob
      const imageBlob = await dataURLToBlob(imageURL);

      // Create FormData and append the Blob with a proper file name
      const formData = new FormData();
      formData.append("image", imageBlob, "resized-image.png");

      // Send to the background-removal server
      const apiResponse = await fetch(`${baseURL}/crop-face`, {
        method: "POST",
        body: formData,
      });

      if (!apiResponse.ok) {
        reset();
        toast.error("Error: No face detected. Please try again.");

        // throw new Error(`API error: ${apiResponse.statusText}`);
      }

      // Convert the response blob to a data URL
      const apiBlob = await apiResponse.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        const serverCroppedDataURL = reader.result as string;
        setSrcImage({ selected: serverCroppedDataURL, temp: "" });
        setOriginalImage(serverCroppedDataURL);
        console.log("Cropped image from server:", serverCroppedDataURL);
      };
      reader.readAsDataURL(apiBlob);
    } catch (error) {
      console.error("Error during server request:", error);
    }
  };

  /**
   * Capture from webcam
   */
  const capture = useCallback(async () => {
    if (!webcamRef.current) return;

    const imageSrc = webcamRef.current.getScreenshot();
    if (!imageSrc) {
      console.error("Failed to capture image");
      return;
    }
    setSrcImage({ selected: imageSrc, temp: "" });
    setOriginalImage(imageSrc);

    try {
      // 1) data URL -> File
      const imageFile = dataURLToFile(imageSrc, "captured_image.jpg");
      // 2) Resize
      const resizedFile = await resizeImageFile(imageFile);

      // 3) Upload
      const formData = new FormData();
      formData.append("image", resizedFile);

      const apiResponse = await fetch(`${baseURL}/crop-face`, {
        method: "POST",
        body: formData,
      });

      if (!apiResponse.ok) {
        reset();
        toast.error("Error: No face detected. Please try again.");

        // throw new Error(`API error: ${apiResponse.statusText}`);
      }

      // 4) Convert blob -> data URL
      const apiBlob = await apiResponse.blob();
      const reader = new FileReader();
      reader.onloadend = () => {
        const serverCroppedDataURL = reader.result as string;
        setSrcImage({ selected: serverCroppedDataURL, temp: "" });
        setOriginalImage(serverCroppedDataURL);
        console.log("Cropped webcam image from server:", serverCroppedDataURL);
      };
      reader.readAsDataURL(apiBlob);
    } catch (error) {
      console.error("Error during cropping from webcam or resizing:", error);
      toast.error("Could not process webcam capture. Reverting to original.");
      setSrcImage({ selected: imageSrc, temp: "" });
      reset();
      // No fallback needed because a webcam screenshot is already a base64 data URL
    }
  }, [webcamRef]);

  /**
   * Actually do the client-side crop
   */
  const makeClientCrop = async (cropValues: any) => {
    if (imageRef.current && cropValues.width && cropValues.height) {
      const croppedImageUrl = await getCroppedImg(
        imageRef.current,
        cropValues,
        `${patronID}.jpeg`,
        fileUrl
      );
      getb64(croppedImageUrl as string, srcImage, setSrcImage);
    }
  };

  /**
   * Done / save
   */
  const saveImages = () => {
    if (!srcImage.selected) {
      toast.error(
        "Error: No image selected. Please capture or upload an image."
      );
      return;
    }

    console.log("Submitting Image:", srcImage.selected);
    didSetImage(srcImage.selected);
    didClose();
    reset();
  };

  /** Icons */
  const UploadImageIcon = (): JSX.Element => (
    <div className="flex flex-col items-center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-8 w-8"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path d="M5.5 13a3.5 3.5 0 01-.369-6.98 4 4 0 117.753-1.977A4.5 4.5 0 1113.5 13H11V9.413l1.293 1.293a1 1 0 001.414-1.414l-3-3a1 1 0 00-1.414 0l-3 3a1 1 0 001.414 1.414L9 9.414V13H5.5z" />
        <path d="M9 13h2v5a1 1 0 11-2 0v-5z" />
      </svg>
      <p className="text-xs text-white">Upload Photo</p>
    </div>
  );

  const CameraIcon = ({ text }: { text: string }): JSX.Element => (
    <div className="flex flex-col items-center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        className="h-10 w-10"
        viewBox="0 0 20 20"
        fill="currentColor"
      >
        <path
          fillRule="evenodd"
          d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z"
          clipRule="evenodd"
        />
      </svg>
      <p className="text-xs text-white">{text}</p>
    </div>
  );

  return (
    <>
      {/* Keep your ToastContainer as before */}
      <ToastContainer
        position="top-center"
        style={{ zIndex: 9999 }}
        closeButton={false}
      />

      {!open ? null : (
        <>
          <div className="top-0 left-0 w-full h-full overflow-y-scroll bg-[rgba(0,0,0,0.5)] fixed drop-shadow-lg">
            <div className="flex flex-col justify-center items-center w-screen h-screen md:h-auto bg-white">
              <div className="flex flex-col items-center md:mt-5">
                {srcImage.selected !== "" ? (
                  <>
                    <img
                      src={srcImage.selected}
                      className="Dropzone-img"
                      alt="selected or processed"
                    />
                  </>
                ) : (
                  <>
                    <Webcam
                      ref={webcamRef}
                      audio={false}
                      screenshotFormat="image/png"
                      mirrored={true}
                      screenshotQuality={1}
                      className="rounded-lg relative"
                      style={webcamStyles}
                      videoConstraints={{
                        width: { ideal: 1920 },
                        height: { ideal: 1080 },
                        facingMode: "user",
                      }}
                      onLoadedData={() => {
                        console.log("Webcam feed is loaded");
                        if (webcamRef.current && webcamRef.current.video) {
                          console.log("setting init bounding box");
                          setCanLoadCamOverlays(true);
                          setWebcamBoundingBox(
                            webcamRef.current.video.getBoundingClientRect()
                          );
                        }
                      }}
                    />
                  </>
                )}
              </div>

              <div className="flex flex-row space-x-5">
                <Button onClick={handleUploadButtonClick}>
                  <UploadImageIcon />
                </Button>

                {srcImage.selected !== "" ? (
                  <Button onClick={reset}>
                    <CameraIcon text="Reset" />
                  </Button>
                ) : (
                  <Button onClick={capture}>
                    <CameraIcon text="Capture" />
                  </Button>
                )}

                {/* {srcImage.selected !== "" && (
                  <Button
                    onClick={
                      isBackgroundRemoved ? revertToOriginal : removeBackground
                    }
                  >
                    <div className="flex flex-col items-center text-xs">
                      <FontAwesomeIcon
                        icon={isBackgroundRemoved ? faRotateLeft : faEraser}
                        className="fa fa-2x h-6 w-6"
                      />
                      {isBackgroundRemoved ? "Revert" : "Remove BG"}
                    </div>
                  </Button>
                )} */}
                {srcImage.selected && (
                  <Button onClick={saveImages} disabled={!srcImage.selected}>
                    Done
                  </Button>
                )}

                {/* Hidden file input */}
                <input
                  type="file"
                  accept="image/*"
                  ref={hiddenFileInputRef}
                  onChange={fileInputChangeHandler}
                  style={{ display: "none" }} // Hides the file input
                />
              </div>
            </div>
          </div>

          <Modal
            show={showCropTool}
            submitButtonText="Crop"
            handleSubmit={(): void => {
              getDimensions(srcImage.temp, setDimensions);
              getSize(srcImage.temp, setSize);
              setShowCropTool(false);
              setSrcImage({ ...srcImage, selected: srcImage.temp });
            }}
            handleClose={(): void => {
              setShowCropTool(false);
              setCrop({
                unit: "%" as "px" | "%",
                width: 30,
                height: 30,
                x: 0,
                y: 0,
              });
              setSrcImage({ ...srcImage, temp: srcImage.selected });
            }}
          >
            <ReactCrop
              crop={crop}
              ruleOfThirds
              onComplete={(cropVal): void => {
                makeClientCrop(cropVal);
              }}
              onChange={(_crop, percentCrop): void => {
                setCrop(percentCrop);
              }}
              className="m-5"
            >
              <img ref={imageRef} src={originalImage} alt="Crop" />
            </ReactCrop>
          </Modal>
        </>
      )}
    </>
  );
};

export default PhotoBoothModal;
